import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    const email = localStorage.getItem('admin_email');
    
    if (token && email) {
      // Verify token is still valid — auto-renews on success
      axios.get(`${API}/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then((res) => {
        // Save renewed token if returned
        if (res.data?.token) {
          localStorage.setItem('admin_token', res.data.token);
        }
        setIsAuthenticated(true);
        setAdmin({ email });
      }).catch((error) => {
        if (error.response && error.response.status === 401) {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_email');
        } else {
          // Network error — keep session alive
          setIsAuthenticated(true);
          setAdmin({ email });
        }
      }).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { token, email: adminEmail } = response.data;
      
      localStorage.setItem('admin_token', token);
      localStorage.setItem('admin_email', adminEmail);
      
      setIsAuthenticated(true);
      setAdmin({ email: adminEmail });
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    setIsAuthenticated(false);
    setAdmin(null);
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, admin, loading, login, logout, getAuthHeaders }}>
      {children}
    </AuthContext.Provider>
  );
};
